import { useState, type FormEvent } from 'react';
import { Button, Card, CardHeader, TextField, Toggle } from '@sparkery/ui';
import type { ArchivePartnerResult, PartnerRow } from '@sparkery/contracts';
import { ApiError } from '../../lib/api.ts';
import { useArchivePartner, useUpdatePartner } from '../../lib/partner-admin.ts';
import { money } from '../../lib/report.tsx';

/**
 * A partner's record: what can be changed, and what cannot.
 *
 * The two immutable facts are shown as facts, not as disabled inputs — a greyed-out
 * field invites an admin to hunt for the permission that would enable it, when the
 * answer is that nothing enables it:
 *
 * - **The link tag**, because it is in every published link and every Analytics row.
 * - **The commission basis** (percentage or fixed fee), because changing it would
 *   rewrite what the partner is owed. A different basis means a new partner record.
 *
 * The rate IS changeable, and the caption says exactly when it takes effect, because
 * "from now on" is ambiguous in a system with a scheduled scrape.
 */
export function PartnerSettingsCard({ partner }: { partner: PartnerRow }) {
  // Both mutations live above the keyed form, so their results survive the remount a
  // successful save causes — and so the archive result, which is the only place the
  // unsettled figure exists, survives the swap to the archived note.
  const update = useUpdatePartner(partner.partnerId);
  const archive = useArchivePartner(partner.partnerId);

  return (
    <div className="partnerDetailRecord">
      {/* KEYED on the saved values: the form remounts and re-seeds itself whenever the
          record moves under it, which is React's own answer to resetting state when a
          prop changes. Synchronising in an effect leaves a window where the inputs and
          the record disagree. */}
      <RecordForm key={signatureOf(partner)} partner={partner} update={update} />

      {partner.archived ? (
        <ArchivedNote partner={partner} justArchived={archive.data ?? null} />
      ) : (
        <ArchiveCard partner={partner} archive={archive} />
      )}
    </div>
  );
}

/** A compact identity for the editable fields: any change remounts the form. */
function signatureOf(partner: PartnerRow): string {
  return [partner.name, partner.adminEmail, partner.commissionRate, partner.workosOrgId].join('|');
}

function RecordForm({
  partner,
  update,
}: {
  partner: PartnerRow;
  update: ReturnType<typeof useUpdatePartner>;
}) {
  const [name, setName] = useState(partner.name);
  const [adminEmail, setAdminEmail] = useState(partner.adminEmail);
  const [rate, setRate] = useState(partner.commissionRate ?? '');

  const dirty =
    name.trim() !== partner.name ||
    adminEmail.trim() !== partner.adminEmail ||
    (partner.commissionAccrues && rate.trim() !== (partner.commissionRate ?? ''));

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!dirty) return;

    update.mutate({
      ...(name.trim() === partner.name ? {} : { name: name.trim() }),
      ...(adminEmail.trim() === partner.adminEmail ? {} : { adminEmail: adminEmail.trim() }),
      ...(partner.commissionAccrues && rate.trim() !== (partner.commissionRate ?? '')
        ? { commissionRate: rate.trim() }
        : {}),
    });
  }

  return (
    <Card variant="panel">
      <form className="partnerDetailForm" onSubmit={submit}>
        <div className="partnerDetailEditGrid">
          <TextField
            id="partner-name"
            label="Partner name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={partner.archived}
          />

          <TextField
            id="partner-email"
            label="Admin email"
            type="email"
            value={adminEmail}
            onChange={(event) => setAdminEmail(event.target.value)}
            disabled={partner.archived}
          />

          <div className="partnerDetailToggleField partnerDetailCommissionToggle">
            <span className="partnerDetailFieldLabel">Commission</span>
            <Toggle
              label="Commission accrues"
              checked={partner.commissionAccrues}
              onChange={() => undefined}
              disabled
              labelHidden
              title="Set at invite, cannot be changed"
            />
          </div>

          <TextField
            id="partner-rate"
            label="Rate"
            value={partner.commissionAccrues ? rate : 'Fixed fee'}
            onChange={(event) => setRate(event.target.value)}
            disabled={partner.archived || !partner.commissionAccrues}
            readOnly={!partner.commissionAccrues}
          />
        </div>

        <span className="partnersFormNote">
          {partner.commissionAccrues
            ? 'Commission accrual was enabled at invite and cannot be changed.'
            : 'This partner is paid a fixed amount agreed separately. Accrual cannot be enabled after invite.'}
        </span>

        {partner.archived ? null : (
          <div className="partnersFormActions partnerDetailSaveActions">
            <Button type="submit" variant="confirm" disabled={!dirty || update.isPending}>
              {update.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        )}
      </form>

      {update.error ? (
        <p className="partnersFormError" role="alert">
          {update.error instanceof ApiError ? update.error.message : 'Could not save that.'}
        </p>
      ) : null}

      {update.data ? (
        <p className="partnersFormOk" role="status">
          {update.data.changed.length > 0
            ? `Saved — ${listOf(update.data.changed)} updated.`
            : 'Nothing had changed, so nothing was saved.'}
        </p>
      ) : null}
    </Card>
  );
}

/**
 * Archiving, behind a typed confirmation.
 *
 * Two things happen at once and both are named before the button is live: no more
 * commission, and no more sign-in. The unsettled balance is NOT a blocker — it still
 * settles into a final statement — but it is stated, because "we archived them and
 * then a statement appeared" is the surprise worth avoiding.
 */
function ArchiveCard({
  partner,
  archive,
}: {
  partner: PartnerRow;
  archive: ReturnType<typeof useArchivePartner>;
}) {
  const [confirmation, setConfirmation] = useState('');
  const confirmed = confirmation.trim().toLowerCase() === partner.name.trim().toLowerCase();

  return (
    <Card>
      <CardHeader title="Archive this partner" meta="Reversible only in the database" />

      <p className="partnersFormNote">
        Stops new commission and removes every seat at the company. Nothing is deleted.
      </p>

      <TextField
        id="archive-confirm"
        label={
          <>
            Type <strong>{partner.name}</strong> to confirm
          </>
        }
        value={confirmation}
        onChange={(event) => setConfirmation(event.target.value)}
        autoComplete="off"
      />

      <div className="partnersFormActions">
        <span className="partnersFormNote" />
        <Button
          variant="danger"
          disabled={!confirmed || archive.isPending}
          onClick={() => archive.mutate()}
        >
          {archive.isPending ? 'Archiving…' : 'Archive partner'}
        </Button>
      </div>

      {archive.error ? (
        <p className="partnersFormError" role="alert">
          {archive.error instanceof ApiError
            ? archive.error.message
            : 'Could not archive that partner.'}
        </p>
      ) : null}
    </Card>
  );
}

function ArchivedNote({
  partner,
  justArchived,
}: {
  partner: PartnerRow;
  /** Set only on the render right after archiving, never on a fresh page load. */
  justArchived: ArchivePartnerResult | null;
}) {
  return (
    <Card>
      <CardHeader
        title="Archived"
        meta={partner.archivedAt ? new Date(partner.archivedAt).toLocaleDateString('en-GB') : ''}
      />
      {justArchived && Number(justArchived.unsettledCommission) !== 0 ? (
        <p className="partnersFormNote">
          {money(justArchived.unsettledCommission)} was unsettled and will appear on a final
          statement.
        </p>
      ) : null}
    </Card>
  );
}

/** "name and admin email", "name, rate and sign-in mapping". */
function listOf(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`;
}
